import { environment } from 'src/environments/environment';
import { InjectionToken, Signal, inject } from '@angular/core';
import type { BillingProfile } from '../models/workspace-billing-profile.model';
import { WorkspaceBillingProfilesFirestoreStore } from './workspace-billing-profiles-firestore-store.service';
import { WorkspaceBillingProfilesMockStore } from './workspace-billing-profiles-mock-store.service';

/**
 * Pattern B contract for Workspace Billing Profiles.
 *
 * Distinct from the user-level `BILLING_PROFILES_STORE` — these profiles belong
 * to a workspace, not a user, and drive contract/invoice/rent-collection flows
 * for all members of the workspace.
 */
export interface IWorkspaceBillingProfilesStore {
  readonly profiles: Signal<readonly BillingProfile[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly defaultProfile: Signal<BillingProfile | null>;
  readonly hasProfiles: Signal<boolean>;

  refresh(): Promise<void>;
  loadBillingProfiles(): void;

  addProfile(
    profile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt' | 'updatedAt'>
  ): Promise<string>;
  updateProfile(
    profileId: string,
    profile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt'>
  ): Promise<void>;
  deleteProfile(profileId: string): Promise<void>;
  setAsDefault(profileId: string): Promise<void>;

  getProfileById(profileId: string): BillingProfile | null;
}

export type WorkspaceBillingProfilesStoreMode = 'mock' | 'firestore';

export function resolveWorkspaceBillingProfilesStoreMode(): WorkspaceBillingProfilesStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('workspaceBillingProfiles.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('workspaceBillingProfiles.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}

export const WORKSPACE_BILLING_PROFILES_STORE = new InjectionToken<IWorkspaceBillingProfilesStore>(
  'WORKSPACE_BILLING_PROFILES_STORE',
  {
    providedIn: 'root',
    factory: () =>
      resolveWorkspaceBillingProfilesStoreMode() === 'mock'
        ? inject(WorkspaceBillingProfilesMockStore)
        : inject(WorkspaceBillingProfilesFirestoreStore)
  }
);
